/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useContext, useEffect, useState} from 'react';
import type {Component} from 'grapesjs';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {getComponentStyleProperty, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import type {OptionListDescriptor, OptionListItem} from './option-list-control.types';
import {OPTION_LIST_KEY_SEPARATOR} from './option-list-control.types';
import styles from './option-list-control.module.scss';

interface OptionListControlInnerProps extends EmailMakerControlProps {
  descriptor: OptionListDescriptor;
}

function itemProperty(item: OptionListItem, descriptor: OptionListDescriptor): string {
  return item.property ?? descriptor.property;
}

function itemKey(item: OptionListItem, descriptor: OptionListDescriptor): string {
  return `${itemProperty(item, descriptor)}${OPTION_LIST_KEY_SEPARATOR}${item.value}`;
}

function isItemValueSetInCss(component: Component, item: OptionListItem, descriptor: OptionListDescriptor): boolean {
  const rawValue = getComponentStyleProperty(component, itemProperty(item, descriptor));
  if (!rawValue) {
    return false;
  }
  return rawValue.split(' ').includes(item.value);
}

function buildInitialSelection(component: Component, descriptor: OptionListDescriptor): Set<string> {
  const selected = new Set<string>();
  descriptor.list.forEach((item) => {
    if (item.selectable === false) {
      return;
    }
    if (isItemValueSetInCss(component, item, descriptor)) {
      selected.add(itemKey(item, descriptor));
    } else if (!descriptor.toggleable && descriptor.defaults && descriptor.defaults === item.value) {
      selected.add(itemKey(item, descriptor));
    }
  });
  return selected;
}

function renderLabel(item: OptionListItem): ReactElement | null {
  if (!item.label) {
    return null;
  }
  return <Text val={item.label} size={TextSize.MD} className={styles.itemLabel} />;
}

function OptionListControlInner({component, descriptor}: OptionListControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);

  useStyleProperty(editor, component, descriptor.property);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => buildInitialSelection(component, descriptor));

  useEffect(() => {
    setSelectedKeys(buildInitialSelection(component, descriptor));
  }, [component, descriptor]);

  /** Optimistic local selection OR live CSS read-back — optimistic-only state (e.g. horizontal-align lives on margins, not the read property) survives. */
  function isItemSelected(item: OptionListItem): boolean {
    return selectedKeys.has(itemKey(item, descriptor)) || isItemValueSetInCss(component, item, descriptor);
  }

  function applyOptimisticSelection(item: OptionListItem): void {
    const key = itemKey(item, descriptor);
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (descriptor.toggleable) {
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      }
      next.clear();
      next.add(key);
      return next;
    });
  }

  function handleItemClick(item: OptionListItem): void {
    if (item.selectable === false || !editor) {
      return;
    }
    applyOptimisticSelection(item);
    item.callback(component, editor);
  }

  function renderItem(item: OptionListItem): ReactElement {
    const isSelected = isItemSelected(item);
    const parentClasses = item.parentClasses ?? '';
    const resolvedName = window.i18next.t(item.name);

    return (
      <li className={`${styles.item} ${parentClasses}`} key={itemKey(item, descriptor)} title={resolvedName}>
        <button
          aria-label={resolvedName}
          aria-pressed={isSelected}
          className={styles.itemButton}
          onClick={(): void => {
            handleItemClick(item);
          }}
          type="button"
        >
          <Icon icon={item.classes} isSelected={isSelected} shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_16} type={IconType.TRANSPARENT} />
          {renderLabel(item)}
        </button>
      </li>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Text val={window.i18next.t(descriptor.titleKey)} size={TextSize.MD} className={styles.title} />
      <ul className={styles.list}>{descriptor.list.map((item) => renderItem(item))}</ul>
    </div>
  );
}

export function makeOptionListControl(descriptor: OptionListDescriptor): EmailMakerControl {
  return createDescriptorControl(OptionListControlInner, descriptor);
}
